import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHome, FaUsers, FaBuilding, FaList, FaTicketAlt, FaUsersCog, FaLayerGroup, FaUserShield } from 'react-icons/fa';
import logo from '../../assets/images/logo.png';
import PulseDot from '../../components/PulseDot';

const menuItems = [
  { path: '/dashboard',       icon: FaHome,       label: 'Dashboard' },
  { path: '/clients',         icon: FaUsers,      label: 'Clientes' },
  { path: '/proyects',        icon: FaBuilding,   label: 'Proyectos' },
  { path: '/proyect_manager', icon: FaList,       label: 'Proyecto Manager' },
  { path: '/support',         icon: FaTicketAlt,  label: 'Soporte' },
  { path: '/admin-usuarios',  icon: FaUsersCog,   label: 'Admin Usuarios' },
  { path: '/admin-planes',    icon: FaLayerGroup, label: 'Planes' },
  { path: '/admin-cuentas',   icon: FaUserShield, label: 'Administradores' },
];

const SideMenu = ({ isOpen, isMobile, toggleSideMenu, setTitle }) => {
  const location = useLocation();

  const getActivePath = () => {
    const parts = location.pathname.replace('/', '').split('/');
    const last = parts[parts.length - 1];
    if (['add', 'edit', 'ticket'].includes(last)) return '/' + parts[0];
    return location.pathname;
  };

  const isActive = (path) => {
    const active = getActivePath();
    return active === path || (path === '/dashboard' && active === '/');
  };

  useEffect(() => {
    const parts = location.pathname.replace('/', '').split('/');
    const last = parts[parts.length - 1];
    let label = menuItems.find(item => item.path === '/' + parts[0])?.label ?? '';
    if (last === 'add')    label += ' / Agregar';
    if (last === 'edit')   label += ' / Editar';
    if (last === 'ticket') label += ' / Ticket';
    setTitle(label);
  }, [location.pathname, setTitle]);

  return (
    <>
      {/* Overlay móvil */}
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            onClick={toggleSideMenu}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        animate={{ x: isOpen ? 0 : -256 }}
        transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
        className="fixed top-0 left-0 h-full w-64 z-40 flex flex-col"
        style={{
          backgroundColor: '#0d0e1e',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          boxShadow: isOpen ? '4px 0 32px rgba(0,0,0,0.6)' : 'none',
        }}
      >
        {/* Logo */}
        <div
          className="flex items-center justify-center py-5 px-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <img src={logo} alt="Cantera Digital" className="sidebar-logo h-10 object-contain" />
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-5 overflow-y-auto space-y-0.5">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-[10px] font-semibold text-[#3A3D52] uppercase tracking-widest px-3 mb-3"
          >
            Operación
          </motion.p>

          {menuItems.map((item, index) => {
            const active = isActive(item.path);
            const Icon = item.icon;

            return (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1], delay: 0.05 + index * 0.04 }}
              >
                <Link
                  to={item.path}
                  onClick={() => isMobile && toggleSideMenu()}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 relative group"
                  style={{ color: active ? '#FFFFFF' : '#7A82A0' }}
                >
                  {/* Active background pill */}
                  {active && (
                    <motion.span
                      layoutId="nav-active"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      className="absolute inset-0 rounded-lg"
                      style={{
                        background: 'linear-gradient(90deg, rgba(220,31,38,0.18), rgba(59,130,246,0.08))',
                        boxShadow: 'inset 3px 0 0 #DC1F26',
                      }}
                    />
                  )}

                  {/* Hover overlay */}
                  {!active && (
                    <span className="absolute inset-0 rounded-lg bg-white/0 group-hover:bg-white/[0.04] transition-colors duration-150" />
                  )}

                  <motion.span
                    whileHover={{ scale: 1.15 }}
                    transition={{ duration: 0.15 }}
                    className="relative z-10"
                  >
                    <Icon
                      size={15}
                      style={{
                        color: active ? '#FFFFFF' : '#4A5270',
                        transition: 'color 0.15s',
                      }}
                      className={!active ? 'group-hover:!text-[#8B92A8]' : ''}
                    />
                  </motion.span>

                  <span
                    className={`relative z-10 ${!active ? 'group-hover:text-[#C8CDD9] transition-colors duration-150' : ''}`}
                  >
                    {item.label}
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </nav>

        {/* Footer */}
        <div
          className="px-5 py-4"
          style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div className="flex items-center gap-2">
            <PulseDot variant="cyan" size={8} />
            <p className="text-[11px] text-[#3A3D52]">Agents v1.0</p>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default SideMenu;
